import React from 'react'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import { fetchCorrelations } from '../../services/informationSystemPhase15Api'
import styles from '../../styles/components/assets/AssetsRiskCompliance.module.scss'

type Tab = 'vulnerabilities' | 'controls' | 'risks'

export default function CorrelationsPanel() {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const [tab, setTab] = React.useState<Tab>('vulnerabilities')
  const [rows, setRows] = React.useState<unknown[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    if (!token) return
    setLoading(true)
    fetchCorrelations(token, tab, orgId)
      .then((r) => setRows(r.correlations || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [token, tab, orgId])

  return (
    <div className={styles.intelligenceCard}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{t('assets.intelligence.correlationsTitle', 'Cross-system correlations')}</h3>
        <div className={styles.tabPills}>
          {(['vulnerabilities', 'controls', 'risks'] as Tab[]).map((k) => (
            <button
              key={k}
              type="button"
              className={`${styles.tabPill} ${tab === k ? styles.tabPillActive : ''}`.trim()}
              onClick={() => setTab(k)}
            >
              {t(`assets.intelligence.correlationTab.${k}`, k)}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.cardBody}>
        {loading ? (
          <p className={styles.loadingText}>{t('common.loading')}</p>
        ) : rows.length === 0 ? (
          <p className={styles.widgetEmpty}>{t('assets.intelligence.noCorrelations', 'No cross-system patterns detected.')}</p>
        ) : (
          <ul className={styles.correlationList}>
            {rows.slice(0, 15).map((row, i) => {
              const r = row as Record<string, unknown>
              const label = String(r.cve || r.controlId || r.title || r.key || i)
              const systems = (r.systems as Array<{ name?: string }>) || []
              return (
                <li key={i} className={styles.correlationItem}>
                  <p className={styles.correlationItemTitle}>{label}</p>
                  <p className={styles.correlationItemMeta}>{String(r.explanation || '')}</p>
                  <p className={styles.correlationItemMeta}>{systems.map((s) => s.name).filter(Boolean).join(', ')}</p>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
